// Single chat message bubble, sent or received.

import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Drafty, Tinode } from 'tinode-sdk';

import Attachment from './attachment.jsx';
import AngkasBrandLogo from './angkas-brand-logo.jsx';
import AngkasScooterIcon from './angkas-scooter-icon.jsx';
import LetterTile from './letter-tile.jsx';
import ReceivedMarker from './received-marker.jsx'

import { fullFormatter } from '../lib/formatters.js';
import { toAbsoluteServerUrl } from '../lib/signed-url.js';
import { sanitizeUrl } from '../lib/utils.js';

/** Same logical Drafty body may arrive as an object (SDK) or JSON string ({ctrl} / storage). */
function contentForDrafty(content) {
  if (content === undefined || content === null) {
    return content;
  }
  if (typeof content !== 'string') {
    return content;
  }
  const s = content.trim();
  if (!s.length || (s.charAt(0) !== '{' && s.charAt(0) !== '[')) {
    return content;
  }
  try {
    const parsed = JSON.parse(content);
    return Drafty.isValid(parsed) ? parsed : content;
  } catch (_) {
    return content;
  }
}

function isSequenceTail(sequence) {
  return sequence === 'single' || sequence === 'last';
}

/**
 * Count emoji in a short emoji-only message (1–5) for larger font sizing.
 *
 * Prefer unicodeSets (`v` + RGI_Emoji). That API ships only with Safari/iOS 17+
 * (and Chromium 112+). Flutter uses WKWebView / System WebView, so iOS 16.x
 * still reaches chat (it has Intl.Segmenter from 16.4) but throws here during
 * plain-text message render — uncaught, which unmounts React (blank screen).
 *
 * Fall back to Extended_Pictographic with the older `u` flag (Safari 11.1+).
 */
function emojiOnlyCount(content) {
  if (typeof content !== 'string' || !content) {
    return 0;
  }

  try {
    if (new RegExp('^\\p{RGI_Emoji}{1,5}$', 'v').test(content)) {
      const matches = content.match(new RegExp('\\p{RGI_Emoji}', 'vg'));
      return matches ? matches.length : 0;
    }
    return 0;
  } catch (_) {
    // iOS < 17 / Chromium < 112: no unicodeSets.
  }

  try {
    // Approximate RGI_Emoji: pictographs plus ZWJ / variation selectors.
    // Not perfect for complex ZWJ sequences, but safe on older WebKit.
    // Use RegExp() (not a literal) so Babel won't expand property escapes.
    if (!new RegExp('^(?:\\p{Extended_Pictographic}|\\p{Emoji_Component}|\\u200d|\\ufe0f)+$', 'u').test(content)) {
      return 0;
    }
    const matches = content.match(new RegExp('\\p{Extended_Pictographic}', 'gu'));
    const n = matches ? matches.length : 0;
    return n >= 1 && n <= 5 ? n : 0;
  } catch (_) {
    return 0;
  }
}

class BaseChatMessage extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      progress: 0
    };

    if (props.uploader) {
      props.uploader.onProgress = this.handleProgress.bind(this);
    }

    this.handleExpandImage = this.handleExpandImage.bind(this);
    this.handlePlayVideo = this.handlePlayVideo.bind(this);
    this.handleFormButtonClick = this.handleFormButtonClick.bind(this);
    this.handleCancelUpload = this.handleCancelUpload.bind(this);
    this.handleQuoteClick = this.handleQuoteClick.bind(this);
    this.retrySend = this.retrySend.bind(this);

    const tinode = props.tinode;
    this.formatterContext = {
      formatMessage: props.intl.formatMessage.bind(props.intl),
      viewportWidth: props.viewportWidth,
      // Wrap authorizeURL so relative server URLs (e.g. `/v0/file/s/...`) are
      // promoted to absolute URLs pointing at the Tinode API server. Without
      // this, the browser would resolve them against the webapp origin which
      // typically differs from the API origin.
      authorizeURL: (url) => toAbsoluteServerUrl(tinode, tinode.authorizeURL(url)),

      onImagePreview: this.handleExpandImage,
      onVideoPreview: this.handlePlayVideo,
      onFormButtonClick: this.handleFormButtonClick,
      onQuoteClick: this.handleQuoteClick
    };
  }

  retrySend() {
    this.props.retrySend(this.props.seq);
  }

  handleExpandImage(e) {
    e.preventDefault();
    this.props.onExpandMedia({
      url: e.target.src,
      filename: e.target.dataset.name,
      width: e.target.dataset.width,
      height: e.target.dataset.height,
      size: e.target.dataset.size,
      type: e.target.dataset.mime
    });
  }

  handlePlayVideo(e) {
    e.preventDefault();
    this.props.onExpandMedia({
      video: true,
      url: e.target.dataset.src,
      preview: e.target.src,
      filename: e.target.dataset.name,
      width: e.target.dataset.width,
      height: e.target.dataset.height,
      duration: e.target.dataset.duration,
      size: e.target.dataset.size,
      type: e.target.dataset.mime
    });
  }

  handleFormButtonClick(e) {
    e.preventDefault();
    const data = {
      seq: this.props.seq
    };
    data.resp = {};
    if (e.target.dataset.name) {
      data.resp[e.target.dataset.name] = e.target.dataset.val ? e.target.dataset.val :
        e.target.dataset.val === undefined ? 1 : '' + e.target.dataset.val;
    }
    if (e.target.dataset.act == 'url') {
      data.ref = sanitizeUrl(e.target.dataset.ref) || 'about:blank';
    }
    const text = e.target.dataset.title || 'unknown';
    this.props.onFormResponse(e.target.dataset.act, text, data);
  }

  handleProgress(ratio) {
    this.setState({progress: ratio});
  }

  handleCancelUpload() {
    this.props.onCancelUpload(this.props.seq, this.props.uploader);
  }

  handleQuoteClick(e) {
    e.preventDefault();
    e.stopPropagation();
    const replyToSeq = this.props.replyToSeq;
    if (replyToSeq) {
      this.props.onQuoteClick(replyToSeq);
    }
  }

  render() {
    const { sequence, response, bubbleRole } = this.props;
    const isTail = isSequenceTail(sequence);
    const showAvatar = this.props.isGroup && response && isTail;
    const sideClass = [
      sequence,
      response ? 'left' : 'right',
      showAvatar && 'with-avatar',
    ].filter(Boolean).join(' ');
    const pubSending = !!this.props.pub?._sending;
    const isFailed = !response && !pubSending &&
      (this.props.received === Tinode.MESSAGE_STATUS_FAILED ||
        this.props.received === Tinode.MESSAGE_STATUS_FATAL);
    const bubbleClass = [
      'bubble',
      bubbleRole && `bubble-${bubbleRole}`,
      isFailed && 'bubble-failed',
    ].filter(Boolean).join(' ');
    const avatar = this.props.userAvatar || true;
    let textSizeClass = 'message-content';
    const fullDisplay =
      // this.props.isGroup &&
      response && isTail;

    let content = contentForDrafty(this.props.content);
    const attachments = [];
    const mime = this.props.mimeType;
    const renderDrafty = Drafty.isValid(content) &&
      (!mime || mime === Drafty.getContentType());
    if (renderDrafty) {
      Drafty.attachments(content, (att, i) => {
        if (att.mime == 'application/json') {
          // Don't show json objects as attachments.
          // They are not meant for users.
          return;
        }
        attachments.push(<Attachment
          tinode={this.props.tinode}
          downloadUrl={Drafty.getDownloadUrl(att)}
          filename={att.name}
          uploading={Drafty.isProcessing(att)}
          mimetype={att.mime}
          size={Drafty.getEntitySize(att)}
          progress={this.state.progress}
          onCancelUpload={this.handleCancelUpload}
          onError={this.props.onError}
          key={i} />);
      }, this);
      const tree = Drafty.format(content, fullFormatter, this.formatterContext);
      content = React.createElement(React.Fragment, null, tree);
    } else if (typeof content == 'string') {
      // Make font bigger for emoji-only messages (no-op on older WebViews).
      const emojiCount = emojiOnlyCount(content);
      if (emojiCount > 0) {
        textSizeClass += ' emoji-' + emojiCount;
      }
    } else {
      content = <><i className='material-icons gray'>warning_amber</i> <i className='gray'>
        <FormattedMessage id='invalid_content'
          defaultMessage='invalid content' description='Shown when the message is unreadable'/></i></>
    }

    const isAutomated = bubbleRole === 'ghost';

    return (
      <li ref={this.props.innerRef} className={sideClass}>
        {this.props.isGroup && this.props.response ? (
          <div className="avatar-box">
            {showAvatar ? (
              isAutomated ? (
                <div className="automated-message-avatar" aria-hidden="true">
                  <AngkasScooterIcon />
                </div>
              ) : (
                <LetterTile
                  tinode={this.props.tinode}
                  topic={this.props.userFrom}
                  title={this.props.userName}
                  avatar={avatar}
                />
              )
            ) : null}
          </div>
        ) : null}
        <div>
          <div className={bubbleClass}>
            <div className='content-meta'>
              {isAutomated ? (
                <div className="bubble-brand">
                  <AngkasBrandLogo />
                </div>
              ) : null}
              <div className={textSizeClass}>
                {content}
                {attachments}
              </div>
            </div>
          </div>
          {this.props.timestamp ? (
            <ReceivedMarker
              edited={this.props.edited}
              timestamp={this.props.timestamp}
              received={this.props.received}
              deliverySyncPending={this.props.deliverySyncPending}
              outgoing={!this.props.response}
              retrySend={this.props.retrySend}
              pub={this.props.pub}
            />
          ) : null}
        </div>
      </li>
    );
  }
};

const IntlChatMessage = injectIntl(BaseChatMessage);
const ChatMessage = React.forwardRef((props, ref) => <IntlChatMessage innerRef = {ref} {...props} />);

export default ChatMessage;
